import { useEffect, useRef, useState } from 'react';
import './App.css';

const DEFAULT_ENTRIES = [
  'Avery',
  'Noah',
  'Lina',
  'Omar',
  'Sana',
  'Julian',
].join('\n');

const DEFAULT_ROUNDS = 5;
const MAX_ROUNDS = 12;
const WHEEL_COLORS = [
  '#f59e0b',
  '#0f766e',
  '#2563eb',
  '#ef4444',
  '#7c3aed',
  '#14b8a6',
  '#ea580c',
  '#1d4ed8',
];
const SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1kIUt8WKS9OYDpwyJDJehFj7ihE26k3Bb91r19A8K6xE/gviz/tq';
const ROUND_KEYWORDS = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
};

function parseEntries(rawValue) {
  const seen = new Set();

  return rawValue
    .split(/\n|,/)
    .map((value) => value.trim())
    .filter(Boolean)
    .filter((name) => {
      const key = name.toLowerCase();

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
}

function sanitizeRounds(value) {
  const parsedValue = Number.parseInt(value, 10);

  if (Number.isNaN(parsedValue)) {
    return DEFAULT_ROUNDS;
  }

  return Math.max(1, Math.min(parsedValue, MAX_ROUNDS));
}

function buildScoreboard(entries, results) {
  const scoreMap = Object.fromEntries(entries.map((entry) => [entry, 0]));

  results.forEach((result) => {
    scoreMap[result.winner] = (scoreMap[result.winner] || 0) + 1;
  });

  return Object.entries(scoreMap)
    .map(([name, wins]) => ({ name, wins }))
    .sort((left, right) => right.wins - left.wins || left.name.localeCompare(right.name));
}

function getLeaders(scoreboard) {
  if (!scoreboard.length) {
    return [];
  }

  const topScore = scoreboard[0].wins;
  return scoreboard.filter((player) => player.wins === topScore).map((player) => player.name);
}

function createWheelGradient(entries) {
  const sliceAngle = 360 / entries.length;

  return `conic-gradient(${entries
    .map((_, index) => {
      const start = index * sliceAngle;
      const end = start + sliceAngle;
      return `${WHEEL_COLORS[index % WHEEL_COLORS.length]} ${start}deg ${end}deg`;
    })
    .join(', ')})`;
}

function createConfettiPieces() {
  return Array.from({ length: 32 }, (_, index) => ({
    id: `${Date.now()}-${index}`,
    color: WHEEL_COLORS[index % WHEEL_COLORS.length],
    x: `${Math.random() * 100}%`,
    drift: `${-90 + Math.random() * 180}px`,
    delay: `${(Math.random() * 0.45).toFixed(2)}s`,
    duration: `${(2.6 + Math.random() * 1.3).toFixed(2)}s`,
    rotation: `${Math.round(Math.random() * 360)}deg`,
    size: `${8 + Math.round(Math.random() * 8)}px`,
  }));
}

function toPositiveInteger(value) {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  const normalizedValue = Math.floor(parsedValue);

  return normalizedValue > 0 ? normalizedValue : null;
}

function normalizePresetValue(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const normalizedValue = String(value).trim();

  return normalizedValue ? normalizedValue : null;
}

function getRoundNumberFromLabel(label, fallbackIndex) {
  const normalizedLabel = label.toLowerCase();

  for (const [keyword, roundNumber] of Object.entries(ROUND_KEYWORDS)) {
    if (normalizedLabel.includes(keyword)) {
      return roundNumber;
    }
  }

  const numericMatch = normalizedLabel.match(/(\d+)/);

  if (numericMatch) {
    return toPositiveInteger(numericMatch[1]);
  }

  return fallbackIndex + 1;
}

function parseScriptedRoundsPayload(payload) {
  const columns = payload.table?.cols || [];
  const cells = payload.table?.rows?.[0]?.c || [];

  return columns.reduce((presets, column, index) => {
    const roundNumber = getRoundNumberFromLabel(column?.label || '', index);
    const winnerKey = normalizePresetValue(cells[index]?.v);

    if (roundNumber && winnerKey) {
      presets[roundNumber] = winnerKey;
    }

    return presets;
  }, {});
}

function getEntryByPreset(entries, presetValue) {
  const normalizedPresetValue = normalizePresetValue(presetValue);

  if (!normalizedPresetValue) {
    return null;
  }

  return (
    entries.find(
      (entry) => normalizePresetValue(entry)?.toLowerCase() === normalizedPresetValue.toLowerCase()
    ) || null
  );
}

function getSpinDurationMs(isScripted) {
  const baseDuration = isScripted ? 5200 : 4300;
  const variance = isScripted ? 1100 : 800;

  return baseDuration + getRandomIndex(variance);
}

function getRandomIndex(maxExclusive) {
  if (maxExclusive <= 1) {
    return 0;
  }

  const cryptoObject = typeof window !== 'undefined' ? window.crypto : null;

  if (cryptoObject?.getRandomValues) {
    const randomBuffer = new Uint32Array(1);
    const maxUint32 = 0x100000000;
    const unbiasedLimit = maxUint32 - (maxUint32 % maxExclusive);

    do {
      cryptoObject.getRandomValues(randomBuffer);
    } while (randomBuffer[0] >= unbiasedLimit);

    return randomBuffer[0] % maxExclusive;
  }

  return Math.floor(Math.random() * maxExclusive);
}

function buildSheetRequestUrl() {
  const requestUrl = new URL(SHEET_URL);

  requestUrl.searchParams.set('tqx', 'out:json');
  requestUrl.searchParams.set('_ts', String(Date.now()));

  return requestUrl.toString();
}

function requestSheetPayload(signal) {
  if (typeof window === 'undefined' || !window.document) {
    return Promise.reject(new Error('The browser document is unavailable.'));
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const mountNode = document.head || document.body || document.documentElement;
    const previousGoogle = window.google;
    const previousVisualization = previousGoogle?.visualization;
    const previousQuery = previousVisualization?.Query;
    const previousSetResponse = previousQuery?.setResponse;
    let settled = false;

    const cleanup = () => {
      if (signal) {
        signal.removeEventListener('abort', handleAbort);
      }

      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }

      if (previousSetResponse) {
        window.google.visualization.Query.setResponse = previousSetResponse;
        return;
      }

      if (previousQuery) {
        delete window.google.visualization.Query.setResponse;
        return;
      }

      if (previousVisualization) {
        delete window.google.visualization.Query;
        return;
      }

      if (previousGoogle) {
        delete window.google.visualization;
        return;
      }

      delete window.google;
    };

    const settle = (callback) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      callback();
    };

    const handleAbort = () => {
      settle(() => reject(new Error('The sheet request was aborted.')));
    };

    if (signal?.aborted) {
      handleAbort();
      return;
    }

    window.google = window.google || {};
    window.google.visualization = window.google.visualization || {};
    window.google.visualization.Query = window.google.visualization.Query || {};
    window.google.visualization.Query.setResponse = (payload) => {
      settle(() => resolve(payload));
    };

    script.async = true;
    script.src = buildSheetRequestUrl();
    script.onerror = () => {
      settle(() => reject(new Error('The sheet script could not be loaded.')));
    };

    signal?.addEventListener('abort', handleAbort, { once: true });
    mountNode.appendChild(script);
  });
}

function App() {
  const [draftEntries, setDraftEntries] = useState(DEFAULT_ENTRIES);
  const [draftRounds, setDraftRounds] = useState(String(DEFAULT_ROUNDS));
  const [entries, setEntries] = useState(parseEntries(DEFAULT_ENTRIES));
  const [rounds, setRounds] = useState(DEFAULT_ROUNDS);
  const [results, setResults] = useState([]);
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [lastWinner, setLastWinner] = useState(null);
  const [winnerDialog, setWinnerDialog] = useState(null);
  const [confettiPieces, setConfettiPieces] = useState([]);
  const [spinDurationMs, setSpinDurationMs] = useState(4800);
  const [scriptedRoundsState, setScriptedRoundsState] = useState({
    error: '',
    presets: {},
    status: 'loading',
  });
  const spinTimerRef = useRef(null);

  const parsedDraftEntries = parseEntries(draftEntries);
  const sanitizedDraftRounds = sanitizeRounds(draftRounds);
  const scoreboard = buildScoreboard(entries, results);
  const leaders = getLeaders(scoreboard);
  const completedRounds = results.filter((result) => result.kind === 'round').length;
  const tiebreakerCount = results.filter((result) => result.kind === 'tiebreaker').length;

  const phase =
    completedRounds < rounds ? 'round' : leaders.length > 1 ? 'tiebreaker' : 'complete';
  const activeWheelEntries = phase === 'tiebreaker' ? leaders : entries;
  const wheelGradient = createWheelGradient(activeWheelEntries);
  const nextRoundLabel =
    phase === 'round'
      ? `Round ${completedRounds + 1} of ${rounds}`
      : phase === 'tiebreaker'
        ? 'Sudden-death tiebreaker'
        : 'Champion decided';
  const spinButtonLabel =
    phase === 'round'
      ? `Spin round ${completedRounds + 1}`
      : phase === 'tiebreaker'
        ? 'Spin tiebreaker'
        : 'Season complete';
  const setupHasChanges =
    parsedDraftEntries.join('\n') !== entries.join('\n') || sanitizedDraftRounds !== rounds;
  const canStartFreshSeason = parsedDraftEntries.length >= 2 && !isSpinning;
  const latestScore = scoreboard[0];
  const leadingSummary =
    latestScore && latestScore.wins > 0
      ? `${latestScore.name} · ${latestScore.wins} win${latestScore.wins === 1 ? '' : 's'}`
      : 'No leader yet';

  async function loadScriptedRounds(signal) {
    setScriptedRoundsState((currentState) => ({
      ...currentState,
      error: '',
      status: 'loading',
    }));

    try {
      const payload = await requestSheetPayload(signal);

      if (signal?.aborted) {
        return;
      }

      setScriptedRoundsState({
        error: '',
        presets: parseScriptedRoundsPayload(payload),
        status: 'ready',
      });
    } catch (error) {
      if (signal?.aborted) {
        return;
      }

      setScriptedRoundsState({
        error: 'Could not load the scripted round feed. Early rounds will stay random.',
        presets: {},
        status: 'error',
      });
    }
  }

  useEffect(() => {
    const abortController = new AbortController();

    loadScriptedRounds(abortController.signal);

    return () => {
      abortController.abort();
      clearTimeout(spinTimerRef.current);
    };
  }, []);

  function closeWinnerDialog() {
    setWinnerDialog(null);
    setConfettiPieces([]);
  }

  function startFreshSeason() {
    if (!canStartFreshSeason) {
      return;
    }

    clearTimeout(spinTimerRef.current);
    setEntries(parsedDraftEntries);
    setRounds(sanitizedDraftRounds);
    setResults([]);
    setRotation(0);
    setSpinDurationMs(4800);
    setIsSpinning(false);
    setLastWinner(null);
    closeWinnerDialog();
  }

  function handleSpin() {
    if (isSpinning || phase === 'complete' || activeWheelEntries.length < 2) {
      return;
    }

    const scriptedValue =
      phase === 'round' ? scriptedRoundsState.presets[completedRounds + 1] || null : null;
    const scriptedWinner = phase === 'round' ? getEntryByPreset(entries, scriptedValue) : null;
    const useScriptedWinner = Boolean(scriptedWinner);
    const winnerIndex = useScriptedWinner
      ? activeWheelEntries.indexOf(scriptedWinner)
      : getRandomIndex(activeWheelEntries.length);
    const winner = activeWheelEntries[winnerIndex];
    const sliceAngle = 360 / activeWheelEntries.length;
    const centerAngle = winnerIndex * sliceAngle + sliceAngle / 2;
    const currentRotation = ((rotation % 360) + 360) % 360;
    const desiredRotation = (360 - centerAngle + 360) % 360;
    const delta = (desiredRotation - currentRotation + 360) % 360;
    const extraTurns = useScriptedWinner ? 7 + getRandomIndex(2) : 6 + getRandomIndex(3);
    const nextSpinDurationMs = getSpinDurationMs(useScriptedWinner);
    const nextRotation = rotation + extraTurns * 360 + delta;
    const nextResult = {
      id: `${Date.now()}-${winner}`,
      kind: phase === 'tiebreaker' ? 'tiebreaker' : 'round',
      order: phase === 'tiebreaker' ? tiebreakerCount + 1 : completedRounds + 1,
      presetValue: useScriptedWinner ? scriptedValue : null,
      source: useScriptedWinner ? 'sheet' : 'random',
      winner,
    };
    const nextResults = [...results, nextResult];

    clearTimeout(spinTimerRef.current);
    setIsSpinning(true);
    setSpinDurationMs(nextSpinDurationMs);
    setRotation(nextRotation);

    spinTimerRef.current = setTimeout(() => {
      const finalScoreboard = buildScoreboard(entries, nextResults);
      const finalLeaders = getLeaders(finalScoreboard);
      const champion = finalScoreboard[0];
      const runnerUpWins = finalScoreboard[1]?.wins || 0;

      setResults(nextResults);
      setLastWinner(nextResult);
      setIsSpinning(false);

      if (completedRounds + (nextResult.kind === 'round' ? 1 : 0) >= rounds && finalLeaders.length === 1) {
        setWinnerDialog({
          name: champion.name,
          wins: champion.wins,
          totalSpins: nextResults.length,
          viaTiebreaker: nextResult.kind === 'tiebreaker',
          margin: champion.wins - runnerUpWins,
        });
        setConfettiPieces(createConfettiPieces());
      }
    }, nextSpinDurationMs);
  }

  return (
    <div className="app-shell">
      <main className="app-frame">
        <section className="hero-panel">
          <div>
            <p className="eyebrow">Rounds-based wheel spinner</p>
            <h1>Round Winner Spinner</h1>
            <p className="hero-copy">
              Run a polished multi-round draw, track every result, and let the wheel settle a
              champion with a final winner reveal.
            </p>
          </div>
          <div className="hero-stats" aria-label="Season summary">
            <article className="hero-stat">
              <span>Active names</span>
              <strong>{entries.length}</strong>
            </article>
            <article className="hero-stat">
              <span>Round plan</span>
              <strong>{rounds}</strong>
            </article>
            <article className="hero-stat">
              <span>Leader</span>
              <strong>{leadingSummary}</strong>
            </article>
          </div>
        </section>

        <section className="app-grid">
          <section className="panel wheel-panel">
            <div className="panel-head">
              <div>
                <p className="panel-kicker">Live wheel</p>
                <h2>{nextRoundLabel}</h2>
              </div>
              <span className={`phase-pill phase-${phase}`}>{phase === 'tiebreaker' ? 'Finalists only' : phase}</span>
            </div>

            <div className="wheel-stage">
              <div className="wheel-pointer" aria-hidden="true" />
              <div
                className="wheel"
                style={{
                  '--spin-duration': `${spinDurationMs}ms`,
                  transform: `rotate(${rotation}deg)`,
                  backgroundImage: wheelGradient,
                }}
              >
                <div className="wheel-sheen" />
                {activeWheelEntries.map((entry, index) => {
                  const sliceAngle = 360 / activeWheelEntries.length;
                  const labelAngle = index * sliceAngle + sliceAngle / 2;

                  return (
                    <div
                      key={entry}
                      className="wheel-label"
                      style={{ '--label-angle': `${labelAngle}deg` }}
                    >
                      <span>{entry}</span>
                    </div>
                  );
                })}

                <div className="wheel-hub">
                  <span>{isSpinning ? 'Spinning now' : 'Latest landing'}</span>
                  <strong>{isSpinning ? '...' : lastWinner?.winner || 'Ready'}</strong>
                </div>
              </div>
            </div>

            <button
              className="primary-button spin-button"
              onClick={handleSpin}
              disabled={isSpinning || phase === 'complete'}
            >
              {isSpinning ? 'Spinning...' : spinButtonLabel}
            </button>

            <div className="status-grid">
              <article className="status-card">
                <span>Season state</span>
                <strong>{nextRoundLabel}</strong>
                
              </article>
              <article className="status-card">
                <span>Last result</span>
                <strong>{lastWinner ? lastWinner.winner : 'No spins yet'}</strong>
                <p>
                  {lastWinner
                    ? lastWinner.source === 'sheet'
                      ? `${lastWinner.kind === 'tiebreaker' ? 'Tiebreaker' : `Round ${lastWinner.order}`} winner `
                      : `${lastWinner.kind === 'tiebreaker' ? 'Tiebreaker' : `Round ${lastWinner.order}`} winner`
                    : 'Spin the wheel to record the opening result.'}
                </p>
              </article>
            </div>
          </section>

          <div className="side-column">
            <section className="panel setup-panel">
              <div className="panel-head">
                <div>
                  <p className="panel-kicker">Season setup</p>
                  <h2>Roster and rounds</h2>
                </div>
              </div>

              <label className="input-label" htmlFor="entries">
                Names
              </label>
              <textarea
                id="entries"
                className="entries-input"
                value={draftEntries}
                onChange={(event) => setDraftEntries(event.target.value)}
                placeholder="One name per line"
              />

              <div className="setup-row">
                <div>
                  <label className="input-label" htmlFor="rounds">
                    Rounds
                  </label>
                  <input
                    id="rounds"
                    className="rounds-input"
                    type="number"
                    min="1"
                    max={String(MAX_ROUNDS)}
                    value={draftRounds}
                    onChange={(event) => setDraftRounds(event.target.value)}
                  />
                </div>
                <div className="setup-meta">
                  <span>Validated roster</span>
                  <strong>{parsedDraftEntries.length} unique names</strong>
                  <p>Changes apply when you start a fresh season.</p>
                </div>
              </div>

              <button
                className="secondary-button"
                onClick={startFreshSeason}
                disabled={!canStartFreshSeason}
              >
                {setupHasChanges ? 'Start fresh season with changes' : 'Reset current season'}
              </button>

              {parsedDraftEntries.length < 2 ? (
                <p className="validation-message">
                  Add at least two unique names before starting a new season.
                </p>
              ) : null}
            </section>

            <section className="panel leaderboard-panel">
              <div className="panel-head">
                <div>
                  <p className="panel-kicker">Leaderboard</p>
                  <h2>Win count</h2>
                </div>
              </div>

              <div className="leaderboard-list">
                {scoreboard.map((player, index) => (
                  <article className="leaderboard-row" key={player.name}>
                    <div className="leaderboard-copy">
                      <span className="leaderboard-rank">{String(index + 1).padStart(2, '0')}</span>
                      <div>
                        <strong>{player.name}</strong>
                        <p>{player.wins === 0 ? 'Waiting for a win' : `${player.wins} recorded`}</p>
                      </div>
                    </div>
                    <div className="leaderboard-value">
                      <span>{player.wins}</span>
                      <small>wins</small>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section className="panel history-panel">
              <div className="panel-head">
                <div>
                  <p className="panel-kicker">History</p>
                  <h2>Round log</h2>
                </div>
              </div>

              {results.length ? (
                <div className="history-list">
                  {[...results].reverse().map((result) => (
                    <article className="history-row" key={result.id}>
                      <div>
                        <span className="history-step">
                          {result.kind === 'tiebreaker'
                            ? 'Tiebreaker'
                            : `${result.source === 'sheet' ? 'Live Spin' : 'Live spin'} · Round ${result.order}`}
                        </span>
                        <strong>{result.winner}</strong>
                      </div>
                      <span className={`history-tag history-${result.kind}`}>{result.kind}</span>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  Results will appear here after the first spin.
                </div>
              )}
            </section>
          </div>
        </section>
      </main>

      {winnerDialog ? (
        <div className="dialog-scrim">
          <section className="winner-dialog" role="dialog" aria-modal="true" aria-labelledby="winner-title">
            <div className="confetti-layer" aria-hidden="true">
              {confettiPieces.map((piece) => (
                <span
                  key={piece.id}
                  className="confetti-piece"
                  style={{
                    '--confetti-x': piece.x,
                    '--confetti-drift': piece.drift,
                    '--confetti-delay': piece.delay,
                    '--confetti-duration': piece.duration,
                    '--confetti-rotation': piece.rotation,
                    width: piece.size,
                    height: `calc(${piece.size} * 1.6)`,
                    backgroundColor: piece.color,
                  }}
                />
              ))}
            </div>

            <button className="dialog-close" type="button" onClick={closeWinnerDialog} aria-label="Close winner dialog">
              Close
            </button>

            <p className="panel-kicker">Season complete</p>
            <h2 id="winner-title">{winnerDialog.name} wins the spinner</h2>
            <p className="dialog-copy">
              {winnerDialog.name} finished with {winnerDialog.wins} wins across{' '}
              {winnerDialog.totalSpins} spin{winnerDialog.totalSpins === 1 ? '' : 's'}
              {winnerDialog.viaTiebreaker ? ' after a sudden-death tiebreaker.' : '.'}
            </p>

            <div className="dialog-stats">
              <article>
                <span>Total rounds</span>
                <strong>{rounds}</strong>
              </article>
              <article>
                <span>Winning margin</span>
                <strong>{winnerDialog.margin}</strong>
              </article>
            </div>

            <button className="primary-button dialog-button" type="button" onClick={closeWinnerDialog}>
              Keep scoreboard open
            </button>
          </section>
        </div>
      ) : null}
    </div>
  );
}

export default App;
